import { useEffect, useState } from 'react';
import type { ViewId } from '../state';
import { useCountdown } from '../hooks/useCountdown';
import { api } from '../lib/api';
import { Icon } from '../components/Icon';
import { isNew } from '../lib/newFeatures';

type WinKind = 'box' | 'fast' | 'big' | 'battle';
type WinRow = {
    kind: WinKind; createdAt: string; amount: number;
    username: string | null; firstName: string | null;
};

type WithdrawRow = {
    id: number; amount: number; method: string; name: string; createdAt: string;
};

type Props = {
    onNavigate: (id: ViewId) => void;
    giveawayTarget: Date;
    notifBadge: number;
};

/**
 * Top-level menu. Three primary tiles plus aggregate stats and the BIG
 * GIVEAWAY teaser (which doubles as a countdown so the user has a reason to
 * come back).
 *
 * Numbers in stats are mock values for the prototype; once the backend
 * exposes `/api/site/stats` we should swap them in via props.
 */
export function MenuView({ onNavigate, giveawayTarget, notifBadge }: Props) {
    const countdown = useCountdown(giveawayTarget);

    // Real menu stats (was hardcoded "10 034 / 74 / 52 130"). 30s cadence
    // is enough — these numbers don't move per-second.
    const [stats, setStats] = useState<{ users: number; online: number; freeBspGiven: number } | null>(null);
    useEffect(() => {
        let cancelled = false;
        const load = async () => {
            try {
                const r = await api.siteStats();
                if (!cancelled) setStats(r);
            } catch { /* offline */ }
        };
        void load();
        const id = window.setInterval(load, 30_000);
        return () => { cancelled = true; window.clearInterval(id); };
    }, []);

    // Cross-game live wins. Polled every 10s — quiet enough not to spam
    // /api/feed/wins, fresh enough to feel "live".
    const [wins, setWins] = useState<WinRow[]>([]);
    useEffect(() => {
        let cancelled = false;
        const load = async () => {
            try {
                const r = await api.feedWins();
                if (!cancelled) setWins(r.items as WinRow[]);
            } catch { /* offline */ }
        };
        void load();
        const id = window.setInterval(load, 10_000);
        return () => { cancelled = true; window.clearInterval(id); };
    }, []);

    // Public withdrawals ticker — "пыль в глаза" per выводы.md. Same polling
    // cadence as the wins feed; quiet refresh so the bar feels alive.
    const [withdraws, setWithdraws] = useState<WithdrawRow[]>([]);
    useEffect(() => {
        let cancelled = false;
        const load = async () => {
            try {
                const r = await api.withdrawalsFeed();
                if (!cancelled) setWithdraws(r.items);
            } catch { /* offline */ }
        };
        void load();
        const id = window.setInterval(load, 30_000);
        return () => { cancelled = true; window.clearInterval(id); };
    }, []);

    return (
        <div className="view view-center">
            <div
                className="menu-card games-card accent"
                onClick={() => onNavigate('games')}
                role="button"
            >
                <span className="card-icon"><Icon name="games" size={38} color="#fff" /></span>
                <span className="card-title">ИГРЫ</span>
                <div className="card-badge">NEW</div>
            </div>

            <div
                className="menu-card small-card accent menu-card-wide"
                onClick={() => onNavigate('bonuses')}
                role="button"
            >
                <span className="card-icon"><Icon name="gift" size={28} color="#fff" /></span>
                <span className="card-title">БОНУСЫ</span>
                {isNew('wheel') && <span className="card-new-sub">🎡 Колесо фортуны</span>}
                {isNew('wheel')
                    ? <div className="card-badge card-badge-new" data-testid="menu-new-wheel">NEW</div>
                    : notifBadge > 0 && <div className="card-badge">{notifBadge}</div>}
            </div>

            <div className="stats-box">
                <div className="stat-row">
                    <span className="stat-label">
                        <Icon name="people" size={14} /> Всего пользователей:
                    </span>
                    <span className="stat-value">
                        {stats ? stats.users.toLocaleString('ru-RU') : '—'}
                    </span>
                </div>
                <div className="stat-row">
                    <span className="stat-label">
                        <span className="online-dot" /> Онлайн в боте:
                    </span>
                    <span className="stat-value">
                        {stats ? stats.online.toLocaleString('ru-RU') : '—'}
                    </span>
                </div>
                <div className="stat-row">
                    <span className="stat-label">
                        <Icon name="gift" size={14} /> Получили бесплатных BSP:
                    </span>
                    <span className="stat-value">
                        {stats ? stats.freeBspGiven.toLocaleString('ru-RU') : '—'}
                    </span>
                </div>
            </div>

            <div
                className="giveaway-btn"
                onClick={() => onNavigate('bigGiveaway')}
                role="button"
            >
                <span className="giveaway-btn-title">
                    BIG BOG GIVEAWAY
                    <Icon name="gift" size={16} color="var(--accent)" style={{ verticalAlign: '-2px', marginLeft: 6 }} />
                </span>
                <span className="menu-timer">{countdown}</span>
            </div>

            {wins.length > 0 && (
                <div className="feed-section">
                    <div className="feed-title">
                        <span>Свежие победы</span>
                        <span className="live-dot">● LIVE</span>
                    </div>
                    <div className="wins-marquee" aria-label="Свежие победы">
                        <div className="wins-marquee-track">
                            {[...wins, ...wins].map((w, i) => (
                                <div
                                    className={`wins-chip kind-${w.kind}`}
                                    key={`${w.createdAt}-${i}`}
                                    title={`${kindLabel(w.kind)} · ${w.amount} BSP`}
                                >
                                    <span className="wins-chip-icon">{kindIcon(w.kind)}</span>
                                    <span className="wins-chip-handle">
                                        {w.username
                                            ? '@' + w.username
                                            : (w.firstName ?? 'Аноним')}
                                    </span>
                                    <span className="wins-chip-amount">
                                        +{w.amount.toLocaleString('ru-RU')}
                                    </span>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            )}

            {withdraws.length > 0 && (
                <div className="feed-section">
                    <div className="feed-title">
                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                            <Icon name="wallet" size={14} /> Свежие выводы
                        </span>
                        <span className="live-dot">● PAID</span>
                    </div>
                    <div className="wins-marquee" aria-label="Свежие выводы">
                        <div className="wins-marquee-track">
                            {[...withdraws, ...withdraws].map((w, i) => (
                                <div
                                    className="wins-chip kind-withdraw"
                                    key={`w-${w.id}-${i}`}
                                    title={`${methodLabel(w.method)} · ${w.amount} BSP`}
                                >
                                    <span className="wins-chip-icon">{methodIcon(w.method)}</span>
                                    <span className="wins-chip-handle">{w.name}</span>
                                    <span className="wins-chip-amount">
                                        −{w.amount.toLocaleString('ru-RU')}
                                    </span>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            )}

            <div className="footer">
                <h1 className="logo">BOGSTARS</h1>
            </div>
        </div>
    );
}

function methodIcon(m: string): string {
    switch (m) {
        case 'stars':     return '⭐';
        case 'ton':       return '💎';
        case 'cryptobot': return '🤖';
        case 'gift':      return '🎁';
        default:          return '💸';
    }
}
function methodLabel(m: string): string {
    switch (m) {
        case 'stars':     return 'Telegram Stars';
        case 'ton':       return 'TON';
        case 'cryptobot': return 'CryptoBot';
        case 'gift':      return 'Telegram Gift';
        default:          return m;
    }
}

function kindIcon(k: WinKind) {
    switch (k) {
        case 'box':    return <Icon name="box" size={14} />;
        case 'fast':   return <Icon name="lightning" size={14} />;
        case 'big':    return <Icon name="gift" size={14} />;
        case 'battle': return <Icon name="sword" size={14} />;
    }
}
function kindLabel(k: WinKind): string {
    switch (k) {
        case 'box':    return 'BSP BOXES';
        case 'fast':   return 'FAST GIVEAWAY';
        case 'big':    return 'BIG GIVEAWAY';
        case 'battle': return 'BOG BATTLE';
    }
}
